import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api, { isExpired } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ImageGallery } from "@/components/listing-detail/ImageGallery";
import { ListingFacts } from "@/components/listing-detail/ListingFacts";
import { ListingActions } from "@/components/listing-detail/ListingActions";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data))
      .catch((e) => setError(e.response?.status === 410 ? "This ad has expired and is no longer available." : "Listing not found."));
  }, [id]);

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8" data-testid="listing-unavailable">
        <div className="border-2 border-black p-12 text-center font-mono-data text-sm uppercase">{error} <Link to="/" className="link-blue">Browse listings →</Link></div>
      </div>
    );
  }

  if (!listing) return <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8 font-mono-data text-xs uppercase">Loading…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 mt-8" data-testid="listing-detail-page">
      <Link to={`/category/${listing.category}`} data-testid="back-link" className="font-mono-data text-xs uppercase link-blue flex items-center gap-1 mb-4">
        <ChevronLeft size={14} /> back to {listing.category?.replace("-", " ")}
      </Link>

      {isExpired(listing) && (
        <div data-testid="owner-expired-banner" className="border-2 border-black bg-[#FF0000] text-white p-3 mb-4 font-mono-data text-xs uppercase">
          This ad has expired and is hidden from the site. <Link to="/dashboard" className="underline text-white">Repost it from your dashboard</Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7">
          <ImageGallery images={listing.images} title={listing.title} />
          <div className="border-2 border-black mt-6 p-4">
            <div className="font-mono-data text-xs uppercase font-bold border-b border-black pb-2 mb-3">/ Description</div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="listing-description">{listing.description}</p>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="border-2 border-black bg-white hard-shadow">
            <ListingFacts listing={listing} />
            <ListingActions listing={listing} user={user} />
          </div>

          <div className="border-2 border-black mt-4 p-3 bg-[#F9F9F9] font-mono-data text-xs">
            <div className="uppercase text-gray-600 mb-1">SAFETY TIP</div>
            <div>Meet in public. Inspect before paying. Never wire money.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
